import React, { useRef, useState } from 'react';
import { Upload, Loader2, FolderOpen } from 'lucide-react';
import {
  UploadDropZone,
  UploadIcon,
  UploadText,
  BrowseButton,
  SupportedFormats,
} from '../uploadDocuments.styles';

import {
  getFilesFromDataTransferItems,
  withRelativePath,
  isHiddenFile,
} from '../utils/getFilesFromDataTransferItems';
// import { MAX_FILE_SIZE_VALUE } from '../../../settings/constant';
import { useAuth } from '../../../hooks/useAuth';
import { formatFileSizeFromBytes } from '../utils';

// onFilesSelect(fileEntries, source) receives an array of
// { file, relativePath } objects — relativePath is the folder chain only
// (no filename), '' for top-level files — plus a `source` tag so the parent
// can tell HOW the files arrived, since relativePath alone can't
// distinguish "Browse Files" from "files dropped individually" (both have
// an empty relativePath):
//   - 'browse-folder' -> the "Choose Folder" input (webkitdirectory)
//   - 'browse-files'  -> the "Browse Files" input (plain multi-file picker)
//   - 'drop'          -> anything dropped onto the zone (may itself contain
//                         folders, in which case relativePath is populated
//                         per-entry the same as a real folder upload)
//
// No count/size/type restrictions here, but hidden files/folders (anything
// starting with '.', e.g. .DS_Store, .git) are always excluded.
// Size validation happens at the parent level for files > 50MB
const DropZone = ({ onFilesSelect, isUploading }) => {
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  const { councilInfo } = useAuth();
  const MAX_FILE_SIZE = councilInfo?.MAX_FILE_SIZE || 50 * 1024 * 1024; // Default to 50MB if not set

  const emit = (fileEntries, source) => {
    const visible = fileEntries.filter((entry) => !isHiddenFile(entry.file));
    if (visible.length > 0) onFilesSelect(visible, source);
  };

  const handleFileChange = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      emit(Array.from(files).map(withRelativePath), 'browse-files');
    }
    e.target.value = '';
  };

  const handleFolderChange = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      emit(Array.from(files).map(withRelativePath), 'browse-folder');
    }
    e.target.value = '';
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.items && e.dataTransfer.items[0]?.webkitGetAsEntry) {
      const files = await getFilesFromDataTransferItems(e.dataTransfer.items);
      emit(files.map(withRelativePath), 'drop');
      return;
    }

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      emit(Array.from(files).map(withRelativePath), 'drop');
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleBrowseFiles = () => fileInputRef.current?.click();
  const handleBrowseFolder = () => folderInputRef.current?.click();

  return (
    <UploadDropZone
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      $isDragging={isDragging}
      $isUploading={isUploading}
    >
      <UploadIcon $isUploading={isUploading}>
        {isUploading ? (
          <Loader2 size={48} strokeWidth={1.5} className="spin" />
        ) : (
          <Upload size={48} strokeWidth={1.5} />
        )}
      </UploadIcon>

      <UploadText>Drag and drop files or a folder here</UploadText>
      <span style={{ color: 'var(--color-text-tertiary)', fontSize: '13px' }}>or</span>

      <div style={{ display: 'flex', gap: '8px' }}>
        <BrowseButton onClick={handleBrowseFiles} disabled={isUploading} type="button">
          Browse Files
        </BrowseButton>
        <BrowseButton onClick={handleBrowseFolder} disabled={isUploading} type="button">
          <FolderOpen size={14} style={{ marginRight: 4, verticalAlign: '-2px' }} />
          Choose Folder
        </BrowseButton>
      </div>

      <SupportedFormats>
        Any file type • Max {formatFileSizeFromBytes(MAX_FILE_SIZE)} per file
      </SupportedFormats>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        onChange={handleFileChange}
        style={{ display: 'none' }}
        aria-hidden="true"
      />

      <input
        ref={folderInputRef}
        type="file"
        multiple
        webkitdirectory=""
        directory=""
        onChange={handleFolderChange}
        style={{ display: 'none' }}
        aria-hidden="true"
      />
    </UploadDropZone>
  );
};

export default DropZone;
